import {detect, fromNavigator} from '@lingui/detect-locale';
import {Locale} from 'date-fns';
import {useEffect, useState} from 'react';
import {importFromEsmSh, importFromJsdelivr} from '../../common/import';
import {configuration} from '../../config';
import {useLogger} from '../../logging/LoggerProvider';
import {DefaultLocale} from '../locales';

export interface LocaleWithCode {
  code: string;
  locale: Locale;
}

export function useLocale(localeCode?: string): LocaleWithCode | undefined {
  const log = useLogger();
  const resolvedLocaleCode = resolveLocaleCode(localeCode);
  const [resolvedLocale, setResolvedLocale] = useState<Locale | undefined>();

  useEffect(() => {
    async function importLocale() {
      let imported = DefaultLocale;

      try {
        imported = await importLocaleFromSource(resolvedLocaleCode);
      } catch (error) /* v8 ignore next -- @preserve */ {
        log.error(`Error importing locale ${resolvedLocaleCode}; falling back to ${DefaultLocale.code}`, error);
      }

      setResolvedLocale(imported);
    }

    if (resolvedLocaleCode === DefaultLocale.code) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setResolvedLocale(DefaultLocale);
    } else {
      void importLocale();
    }
  }, [log, resolvedLocaleCode]);

  return (
    resolvedLocale && {
      code: resolvedLocale.code,
      locale: resolvedLocale,
    }
  );
}

function resolveLocaleCode(locale?: string): string {
  return locale ?? detectLocale() ?? DefaultLocale.code;
}

export function detectLocale(): string | null {
  return detect(fromNavigator());
}

function importLocaleFromSource(localeCode: string): Promise<Locale> {
  switch (configuration.localeSource) {
    case 'bundle':
      return importLocaleFromBundle(localeCode);
    /* v8 ignore start -- @preserve */
    case 'esmsh':
      return importFromEsmSh(
        {name: 'date-fns', path: `/locale/${localeCode}`, version: DATEFNS_VERSION},
        localeCode.replaceAll('-', ''),
      );
    case 'jsdelivr':
      return importFromJsdelivr(
        {name: 'date-fns', path: `/locale/${localeCode}`, version: DATEFNS_VERSION},
        localeCode.replaceAll('-', ''),
      );
    /* v8 ignore stop */
  }
}

async function importLocaleFromBundle(localeCode: string): Promise<Locale> {
  // Import individual locale to avoid bundling all date-fns locales:
  const imported = (await import(/* @vite-ignore */ `date-fns/locale/${localeCode}`)) as {default: Locale};
  return imported.default;
}
